import React, { useEffect, useState, useCallback } from 'react';
import { suppliesAPI, api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatPhone } from '@/lib/utils';
import { tokenizeCardWithIntuit } from '@/lib/intuitTokenizer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Loader2, CalendarClock, AlertTriangle, CheckCircle2, XCircle,
  Package, Trash2, ChevronDown, ChevronUp, Clock, CreditCard, DollarSign,
  RotateCcw,
} from 'lucide-react';

// Lightweight requests API — kept inline so we don't pollute lib/api.js for
// a single tab. If Phase 3 grows more request endpoints we can promote it.
const requestsAPI = {
  list:    (params) => api.get('/supplies/requests', { params }),
  get:     (id) => api.get(`/supplies/requests/${id}`),
  create:  (payload) => api.post('/supplies/requests', payload),
  cancel:  (id) => api.post(`/supplies/requests/${id}/cancel`),
  approve: (id, payload) => api.post(`/supplies/requests/${id}/approve`, payload),
  reject:  (id, payload) => api.post(`/supplies/requests/${id}/reject`, payload),
  markReady: (id) => api.post(`/supplies/requests/${id}/mark-ready`),
  startPreparing: (id) => api.post(`/supplies/requests/${id}/start-preparing`),
  markReadyForPickup: (id) => api.post(`/supplies/requests/${id}/mark-ready-for-pickup`),
  markPickedUp: (id) => api.post(`/supplies/requests/${id}/mark-picked-up`),
  chargeDamage: (id, payload) => api.post(`/supplies/requests/${id}/charge-damage`, payload),
  refundDeposit: (id, payload = {}) => api.post(`/supplies/requests/${id}/refund-deposit`, payload),
  tokenizationConfig: () => api.get('/supplies/requests/tokenization-config'),
  quickEvent: (payload) => api.post('/supplies/requests/quick-event', payload),
  lookupAgents: (q) => api.get('/supplies/requests/lookup/agents', { params: { q } }),
  lookupStaff:  (q) => api.get('/supplies/requests/lookup/staff', { params: { q } }),
  lookupEvents: (q) => api.get('/supplies/requests/lookup/events', { params: { q } }),
  previewConflict: (params) => api.get('/supplies/requests/preview/conflicts', { params }),
  previewDeposit: (payload) => api.post('/supplies/requests/preview/deposit', payload),
  availableAssets: (params) => api.get('/supplies/requests/available-assets/lookup', { params }),
};

const STATUS_STYLES = {
  pending:          { label: 'Pending',           cls: 'bg-amber-100 text-amber-800' },
  approved:         { label: 'Approved',          cls: 'bg-sky-100 text-sky-800' },
  ready:            { label: 'Ready',             cls: 'bg-indigo-100 text-indigo-800' },
  preparing:        { label: 'Preparing',         cls: 'bg-violet-100 text-violet-800' },
  ready_for_pickup: { label: 'Ready for Pickup',  cls: 'bg-teal-100 text-teal-800' },
  picked_up:        { label: 'Picked Up',         cls: 'bg-emerald-100 text-emerald-800' },
  checked_out:      { label: 'Checked Out',       cls: 'bg-emerald-100 text-emerald-800' },
  returned:         { label: 'Returned',          cls: 'bg-slate-200 text-slate-700' },
  rejected:         { label: 'Rejected',          cls: 'bg-rose-100 text-rose-800' },
  cancelled:        { label: 'Cancelled',         cls: 'bg-slate-200 text-slate-600' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { label: status, cls: 'bg-slate-100' };
  return <Badge className={s.cls}>{s.label}</Badge>;
}

function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

// Simple debounced type-ahead combobox — used for agent + event lookups.
function TypeaheadCombobox({ label, placeholder, value, onSelect, fetcher,
                             renderItem, testId, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await fetcher(query);
        setItems(data.items || []);
      } catch { /* silent */ }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [open, query, fetcher]);
  return (
    <div className="relative space-y-1.5">
      {label && <Label>{label}</Label>}
      <div className="relative">
        <Input
          value={value ? value.label : query}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); onSelect(null); }}
          placeholder={placeholder}
          data-testid={testId}
        />
        {loading && <Loader2 size={12} className="animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />}
      </div>
      {open && !disabled && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
            onMouseDown={(e) => e.preventDefault()}>
          {items.length === 0 && !loading && (
            <li className="px-3 py-2 text-sm text-slate-400">No matches</li>
          )}
          {items.map((it, i) => (
            <li key={i}
                onClick={() => { onSelect(it); setOpen(false); setQuery(''); }}
                className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-100"
                data-testid={`${testId}-option-${i}`}>
              {renderItem(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Create Request Dialog
// ═══════════════════════════════════════════════════════════════════
function CreateRequestDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    event_date: '', return_date: '', event_label: '', notes: '',
    for_agent: null,       // { npn, first_name, last_name, ... } | null
    for_staff: null,       // { user_id, full_name, ... } | null
    linked_event: null,    // { event_id, title, ... } | null
    target_kind: 'agent',  // 'agent' | 'staff' — KR admin only
    pickup_type: 'kr_office',
    pickup_location_id: '',
    pickup_window_start: '',
    pickup_window_end: '',
    return_location_id: '',
    save_card_on_file: true,
    financial_responsibility_accepted: false,
    // Card capture fields — PCI: these values NEVER touch KR's backend.
    // They are tokenized in-browser via Intuit's tokens endpoint, and
    // only the opaque token is sent with the request payload.
    card_number: '',
    card_exp_month: '',
    card_exp_year: '',
    card_cvc: '',
    card_name: '',
    card_postal: '',
    items: [{ type_id: '', quantity: 1 }],
  });
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState({});
  const [lead, setLead] = useState(null);
  const [tokenizationUrl, setTokenizationUrl] = useState(null);
  const [tokenizationError, setTokenizationError] = useState(null);
  const [autoDeposit, setAutoDeposit] = useState({ amount: 0, cap: 65 });

  useEffect(() => {
    if (!open) return;
    suppliesAPI.listTypes().then(({ data }) => setTypes(data.items || [])).catch(() => {});
    suppliesAPI.listLocations().then(({ data }) => setLocations(data.items || [])).catch(() => {});
    // Fetch tokenization config once when the dialog opens. Silently
    // fall back to a disabled card block when QuickBooks isn't connected.
    setTokenizationError(null);
    setTokenizationUrl(null);
    requestsAPI.tokenizationConfig()
      .then(({ data }) => setTokenizationUrl(data.tokenization_url))
      .catch((e) => setTokenizationError(
        e?.response?.data?.detail || 'QuickBooks not connected — deposit card cannot be captured.'
      ));
    setForm({
      event_date: '', return_date: '', event_label: '', notes: '',
      for_agent: null, for_staff: null, linked_event: null, target_kind: 'agent',
      pickup_type: 'kr_office', pickup_location_id: '',
      pickup_window_start: '', pickup_window_end: '',
      return_location_id: '',
      save_card_on_file: true,
      financial_responsibility_accepted: false,
      card_number: '', card_exp_month: '', card_exp_year: '',
      card_cvc: '', card_name: '', card_postal: '',
      items: [{ type_id: '', quantity: 1 }],
    });
    setConflicts({});
    setLead(null);
  }, [open]);

  // Estimate business-day lead time client-side to show the urgent warning.
  useEffect(() => {
    if (!form.event_date) { setLead(null); return; }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ev = new Date(form.event_date + 'T00:00:00');
    if (Number.isNaN(ev.getTime())) { setLead(null); return; }
    let count = 0;
    const cur = new Date(today);
    cur.setDate(cur.getDate() + 1);
    while (cur <= ev) {
      const d = cur.getDay();
      if (d >= 1 && d <= 5) count += 1;
      cur.setDate(cur.getDate() + 1);
    }
    setLead(count);
  }, [form.event_date]);

  // Live conflict preview whenever dates + a line's type/qty are ready
  useEffect(() => {
    if (!form.event_date || !form.return_date) return;
    let cancelled = false;
    (async () => {
      const next = {};
      for (let i = 0; i < form.items.length; i += 1) {
        const it = form.items[i];
        if (!it.type_id || !it.quantity) continue;
        try {
          const { data } = await requestsAPI.previewConflict({
            event_date: form.event_date,
            return_date: form.return_date,
            type_id: it.type_id,
            quantity: Number(it.quantity),
          });
          if (!cancelled) next[i] = data;
        } catch { /* ignore */ }
      }
      if (!cancelled) setConflicts(next);
    })();
    return () => { cancelled = true; };
  }, [form.event_date, form.return_date, form.items]);

  // Auto-compute deposit whenever the item list changes.
  useEffect(() => {
    const validItems = form.items
      .filter((it) => it.type_id && Number(it.quantity) > 0)
      .map((it) => ({ type_id: it.type_id, quantity: Number(it.quantity) }));
    if (validItems.length === 0) { setAutoDeposit({ amount: 0, cap: 65 }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await requestsAPI.previewDeposit({ items: validItems });
        if (!cancelled) setAutoDeposit(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [form.items]);

  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { type_id: '', quantity: 1 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm((f) => ({
    ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
  }));

  const depositAmount = autoDeposit.amount || 0;
  const cardFilled = form.card_number && form.card_exp_month && form.card_exp_year && form.card_cvc;
  const wantsCharge = depositAmount > 0;
  const targetChosen = !isAdmin || (form.target_kind === 'agent' ? !!form.for_agent : !!form.for_staff);
  const canSubmit = form.event_date && form.return_date
    && form.items.length > 0 && form.items.every((it) => it.type_id && Number(it.quantity) > 0)
    && form.financial_responsibility_accepted
    && targetChosen
    && (!wantsCharge || (cardFilled && tokenizationUrl));

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Complete the target, dates, items, financial responsibility and card details.');
      return;
    }
    setSaving(true);
    try {
      // Step 1: tokenize the card DIRECTLY with Intuit if a deposit is set.
      // The PAN/CVV never touch KR's backend — only the opaque token does.
      let paymentToken = null;
      let cardBrand = null;
      let cardLast4 = null;
      if (wantsCharge && tokenizationUrl && cardFilled) {
        try {
          const tok = await tokenizeCardWithIntuit({
            tokenizationUrl,
            card: {
              number: form.card_number,
              expMonth: form.card_exp_month,
              expYear: form.card_exp_year,
              cvc: form.card_cvc,
              name: form.card_name || form.for_agent?.first_name || '',
            },
          });
          paymentToken = tok.value;
          cardBrand = tok.card.brand;
          cardLast4 = tok.card.last4;
        } catch (tokErr) {
          toast.error(`Card capture failed: ${tokErr.message}`);
          setSaving(false);
          return;
        }
      }

      const payload = {
        event_date: form.event_date,
        return_date: form.return_date,
        event_label: form.event_label.trim() || (form.linked_event?.title || null),
        event_id: form.linked_event?.event_id || null,
        notes: form.notes.trim() || null,
        pickup_type: form.pickup_type,
        pickup_location_id: form.pickup_location_id || null,
        pickup_window_start: form.pickup_window_start || null,
        pickup_window_end: form.pickup_window_end || null,
        return_location_id: form.return_location_id || null,
        save_card_on_file: form.save_card_on_file,
        deposit_payment_token: paymentToken,
        deposit_card_brand: cardBrand,
        deposit_card_last4: cardLast4,
        financial_responsibility_accepted: form.financial_responsibility_accepted,
        items: form.items.map((it) => ({ type_id: it.type_id, quantity: Number(it.quantity) })),
      };
      if (isAdmin) {
        if (form.target_kind === 'agent' && form.for_agent) {
          payload.for_agent_npn = form.for_agent.npn;
        } else if (form.target_kind === 'staff' && form.for_staff) {
          payload.for_staff_user_id = form.for_staff.user_id;
        }
      }
      await requestsAPI.create(payload);
      toast.success(paymentToken
        ? 'Request submitted — card captured for deposit'
        : 'Request submitted');
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSaving(false);
    }
  };

  const pickupLocations = locations.filter((l) => !l.archived
    && (l.kind === 'pickup_partner' || l.kind === 'warehouse'));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Equipment Request</DialogTitle>
          <DialogDescription>
            {isAdmin
              ? 'KR Admin: create an order on behalf of an agent or staff member. NPN and staff selection are validated against the database.'
              : 'Requests submitted with less than 5 business days lead time are marked URGENT and still need KR approval.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Target selection (KR admin only) */}
          {isAdmin && (
            <div className="border rounded-md p-3 space-y-3 bg-slate-50/50">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Who is this for?</div>
              <div className="flex gap-2">
                <Button type="button" variant={form.target_kind === 'agent' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setForm({ ...form, target_kind: 'agent', for_staff: null })}
                        data-testid="target-agent-btn">
                  Agent (with NPN)
                </Button>
                <Button type="button" variant={form.target_kind === 'staff' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setForm({ ...form, target_kind: 'staff', for_agent: null })}
                        data-testid="target-staff-btn">
                  Staff
                </Button>
              </div>
              {form.target_kind === 'agent' ? (
                <TypeaheadCombobox
                  label="Agent — required (search by name / NPN / email)"
                  placeholder="Start typing an agent name or NPN..."
                  value={form.for_agent && {
                    label: `${form.for_agent.first_name || ''} ${form.for_agent.last_name || ''} · NPN ${form.for_agent.npn}`
                  }}
                  onSelect={(a) => setForm({ ...form, for_agent: a })}
                  fetcher={(q) => requestsAPI.lookupAgents(q)}
                  renderItem={(a) => (
                    <div>
                      <div className="font-medium">
                        {a.first_name} {a.last_name} <span className="text-xs text-slate-500">NPN {a.npn}</span>
                      </div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                    </div>
                  )}
                  testId="agent-lookup"
                />
              ) : (
                <TypeaheadCombobox
                  label="Staff — required"
                  placeholder="Start typing a staff name..."
                  value={form.for_staff && {
                    label: form.for_staff.full_name || form.for_staff.name || form.for_staff.email,
                  }}
                  onSelect={(s) => setForm({ ...form, for_staff: s })}
                  fetcher={(q) => requestsAPI.lookupStaff(q)}
                  renderItem={(s) => (
                    <div>
                      <div className="font-medium">{s.full_name || s.name || s.email}</div>
                      <div className="text-xs text-slate-500">{s.email}</div>
                    </div>
                  )}
                  testId="staff-lookup"
                />
              )}
            </div>
          )}

          {/* Linked event */}
          {isAdmin && (
            <TypeaheadCombobox
              label="Linked event (optional — search existing events)"
              placeholder="Search events by title..."
              value={form.linked_event && { label: `${form.linked_event.title} · ${form.linked_event.event_date}` }}
              onSelect={(e) => setForm({
                ...form,
                linked_event: e,
                event_label: e?.title || form.event_label,
                event_date: e?.event_date || form.event_date,
              })}
              fetcher={(q) => requestsAPI.lookupEvents(q)}
              renderItem={(e) => (
                <div>
                  <div className="font-medium">{e.title}</div>
                  <div className="text-xs text-slate-500">{e.event_date}{e.address ? ` · ${e.address}` : ''}</div>
                </div>
              )}
              testId="event-lookup"
            />
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Event Date *</Label>
              <Input type="date" value={form.event_date}
                     onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                     data-testid="request-event-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Return Date *</Label>
              <Input type="date" value={form.return_date}
                     onChange={(e) => setForm({ ...form, return_date: e.target.value })}
                     data-testid="request-return-date" />
            </div>
          </div>
          {form.event_date && lead !== null && lead < 5 && (
            <div className="text-xs bg-orange-50 border border-orange-300 text-orange-900 rounded px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
              <span>
                <strong>URGENT:</strong> {lead} business day(s) until the event.
                The request will still be submitted and marked as urgent, but approval is not guaranteed.
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Event Label</Label>
            <Input value={form.event_label} onChange={(e) => setForm({ ...form, event_label: e.target.value })}
                   placeholder="e.g. AEP Kickoff at Orlando Community Center" />
          </div>

          {/* Pickup */}
          <div className="border rounded-md p-3 space-y-3 bg-slate-50/50">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Pickup</div>
            <div className="grid grid-cols-3 gap-2">
              <Button type="button" variant={form.pickup_type === 'kr_office' ? 'default' : 'outline'} size="sm"
                      onClick={() => setForm({ ...form, pickup_type: 'kr_office' })}>KR Office</Button>
              <Button type="button" variant={form.pickup_type === 'partner_location' ? 'default' : 'outline'} size="sm"
                      onClick={() => setForm({ ...form, pickup_type: 'partner_location' })}>Partner Location</Button>
              <Button type="button" variant={form.pickup_type === 'staff_meetup' ? 'default' : 'outline'} size="sm"
                      onClick={() => setForm({ ...form, pickup_type: 'staff_meetup' })}>Meet with Staff</Button>
            </div>
            {(form.pickup_type === 'kr_office' || form.pickup_type === 'partner_location') && (
              <div className="space-y-1.5">
                <Label>Location</Label>
                <Select value={form.pickup_location_id || 'none'}
                        onValueChange={(v) => setForm({ ...form, pickup_location_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="pickup-location-select">
                    <SelectValue placeholder="Choose location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {pickupLocations.map((l) => (
                      <SelectItem key={l.location_id} value={l.location_id}>
                        {l.name} {l.kind === 'pickup_partner' ? '· Partner' : '· KR'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Pickup Window Start</Label>
                <Input type="datetime-local" value={form.pickup_window_start}
                       onChange={(e) => setForm({ ...form, pickup_window_start: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Pickup Window End</Label>
                <Input type="datetime-local" value={form.pickup_window_end}
                       onChange={(e) => setForm({ ...form, pickup_window_end: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Items *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLine}>
                <Plus size={14} className="mr-1" /> Add item
              </Button>
            </div>
            {form.items.map((it, i) => {
              const conflict = conflicts[i];
              return (
                <div key={i} className="border rounded-md p-3 space-y-2 bg-slate-50/50">
                  <div className="grid grid-cols-[1fr,120px,40px] gap-2 items-end">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={it.type_id} onValueChange={(v) => updateLine(i, { type_id: v })}>
                        <SelectTrigger data-testid={`request-item-type-${i}`}>
                          <SelectValue placeholder="Choose equipment type" />
                        </SelectTrigger>
                        <SelectContent>
                          {types.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Quantity</Label>
                      <Input type="number" min="1" max="200" value={it.quantity}
                             onChange={(e) => updateLine(i, { quantity: e.target.value })}
                             data-testid={`request-item-qty-${i}`} />
                    </div>
                    <Button type="button" variant="ghost" size="sm"
                            className="text-rose-600"
                            disabled={form.items.length === 1}
                            onClick={() => removeLine(i)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  {conflict && conflict.shortfall > 0 && (
                    <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 flex items-start gap-1.5">
                      <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                      <span>
                        Only {conflict.available_now} of {conflict.total_in_inventory}{' '}
                        {conflict.type_name || 'unit(s)'} available in this window
                        ({conflict.overlapping_committed} committed elsewhere).
                        <strong> Shortfall: {conflict.shortfall}.</strong>
                      </span>
                    </div>
                  )}
                  {conflict && conflict.shortfall === 0 && (
                    <div className="text-xs text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 size={12} />
                      {conflict.available_now} available for this window.
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Deposit (auto-computed — agent does not choose) */}
          <div className="border rounded-md p-3 space-y-3 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Security Deposit</div>
              <CreditCard size={14} className="text-slate-400" />
            </div>
            <div className="flex items-center justify-between px-3 py-2 border rounded-md bg-white">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Auto-computed</div>
                <div className="text-2xl font-semibold text-slate-900" data-testid="auto-deposit-amount">
                  ${depositAmount.toFixed(2)}
                </div>
                <div className="text-xs text-slate-500">
                  Based on selected items · capped at ${autoDeposit.cap?.toFixed(2) || '65.00'}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.save_card_on_file}
                       onChange={(e) => setForm({ ...form, save_card_on_file: e.target.checked })} />
                Save card on file
              </label>
            </div>
            {wantsCharge && tokenizationError && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 flex items-start gap-1.5">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                <span>{tokenizationError}</span>
              </div>
            )}
            {wantsCharge && tokenizationUrl && (
              <div className="space-y-2 pt-1">
                <div className="text-xs text-slate-600">
                  Card is captured securely — <strong>KR never sees the card number</strong>.
                  The deposit is charged when the order is marked Ready for Pickup.
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Cardholder Name</Label>
                  <Input value={form.card_name}
                         onChange={(e) => setForm({ ...form, card_name: e.target.value })}
                         placeholder="Full name as printed on the card"
                         data-testid="card-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Card Number *</Label>
                  <Input inputMode="numeric" autoComplete="cc-number"
                         value={form.card_number}
                         onChange={(e) => setForm({ ...form, card_number: e.target.value.replace(/[^\d\s]/g, '') })}
                         placeholder="4111 1111 1111 1111"
                         data-testid="card-number-input" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Exp Month *</Label>
                    <Input inputMode="numeric" maxLength={2}
                           value={form.card_exp_month}
                           onChange={(e) => setForm({ ...form, card_exp_month: e.target.value.replace(/\D/g, '') })}
                           placeholder="MM"
                           data-testid="card-exp-month-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Exp Year *</Label>
                    <Input inputMode="numeric" maxLength={4}
                           value={form.card_exp_year}
                           onChange={(e) => setForm({ ...form, card_exp_year: e.target.value.replace(/\D/g, '') })}
                           placeholder="YYYY"
                           data-testid="card-exp-year-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">CVC *</Label>
                    <Input inputMode="numeric" maxLength={4}
                           value={form.card_cvc}
                           onChange={(e) => setForm({ ...form, card_cvc: e.target.value.replace(/\D/g, '') })}
                           placeholder="123"
                           data-testid="card-cvc-input" />
                  </div>
                </div>
              </div>
            )}
            {!wantsCharge && (
              <p className="text-xs text-slate-500">
                No deposit for this item selection. Add items with a set deposit to trigger card capture.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} placeholder="Special instructions, delivery notes..." />
          </div>

          {/* Financial responsibility acknowledgement */}
          <label className="flex items-start gap-2 p-3 border-2 border-slate-200 rounded-md bg-white cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.financial_responsibility_accepted}
              onChange={(e) => setForm({ ...form, financial_responsibility_accepted: e.target.checked })}
              data-testid="financial-responsibility-checkbox"
            />
            <span className="text-sm text-slate-700">
              <strong>I understand</strong> that I (or the agent this order is for) am financially responsible
              for any loss or damage to the equipment while it is checked out.
              The deposit on file may be charged for damages assessed at return.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="request-submit">
            {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Admin Approval Dialog
// ═══════════════════════════════════════════════════════════════════
function ApproveDialog({ open, onOpenChange, request, onDone }) {
  const [available, setAvailable] = useState({}); // type_id → assets[]
  const [picks, setPicks] = useState({}); // type_id → selected asset_ids[]
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !request) return;
    setNotes('');
    setPicks({});
    setLoading(true);
    (async () => {
      const next = {};
      for (const line of request.items) {
        try {
          const { data } = await requestsAPI.availableAssets({
            type_id: line.type_id,
            event_date: request.event_date,
            return_date: request.return_date,
          });
          next[line.type_id] = data.items || [];
        } catch {
          next[line.type_id] = [];
        }
      }
      setAvailable(next);
      setLoading(false);
    })();
  }, [open, request]);

  const togglePick = (typeId, assetId, needed) => {
    setPicks((p) => {
      const cur = new Set(p[typeId] || []);
      if (cur.has(assetId)) cur.delete(assetId);
      else if (cur.size < needed) cur.add(assetId);
      return { ...p, [typeId]: Array.from(cur) };
    });
  };

  const allFulfilled = request?.items?.every(
    (line) => (picks[line.type_id]?.length || 0) === line.quantity,
  );

  const submit = async () => {
    if (!allFulfilled) {
      toast.error('Assign the correct number of assets for each line.');
      return;
    }
    setSaving(true);
    try {
      const assignments = request.items.map((line) => ({
        type_id: line.type_id,
        asset_ids: picks[line.type_id],
      }));
      await requestsAPI.approve(request.request_id, {
        assignments, admin_notes: notes.trim() || null,
      });
      toast.success('Request approved');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setSaving(false);
    }
  };

  if (!request) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Approve Request</DialogTitle>
          <DialogDescription>
            Assign specific assets to each line. Selected assets will be marked as reserved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-2" />
              Loading available assets...
            </div>
          ) : (
            request.items.map((line) => {
              const opts = available[line.type_id] || [];
              const chosen = picks[line.type_id] || [];
              return (
                <div key={line.type_id} className="border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">{line.type_name}</div>
                      <div className="text-xs text-slate-500">
                        Need {line.quantity} — {chosen.length}/{line.quantity} selected
                      </div>
                    </div>
                    <Badge className={chosen.length === line.quantity ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
                      {opts.length} available
                    </Badge>
                  </div>
                  {opts.length === 0 ? (
                    <p className="text-sm text-rose-600">No free assets of this type for this window.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {opts.map((a) => {
                        const isPicked = chosen.includes(a.asset_id);
                        const disabled = !isPicked && chosen.length >= line.quantity;
                        return (
                          <button
                            key={a.asset_id}
                            type="button"
                            disabled={disabled}
                            onClick={() => togglePick(line.type_id, a.asset_id, line.quantity)}
                            className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${
                              isPicked
                                ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
                                : disabled
                                  ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                                  : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                            }`}
                            data-testid={`approve-pick-${a.asset_id}`}
                          >
                            <div className="font-medium">{a.label || '—'}</div>
                            <div className="text-xs font-mono text-slate-500">{a.ean13}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div className="space-y-1.5">
            <Label>Admin notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                      placeholder="Internal notes visible to KR staff only" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!allFulfilled || saving} data-testid="approve-submit">
            {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
            Approve & Reserve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Reject Dialog
// ═══════════════════════════════════════════════════════════════════
function RejectDialog({ open, onOpenChange, request, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error('Please provide a reason (min 3 chars).');
      return;
    }
    setSaving(true);
    try {
      await requestsAPI.reject(request.request_id, { reason: reason.trim() });
      toast.success('Request rejected');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setSaving(false);
    }
  };

  if (!request) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Request</DialogTitle>
          <DialogDescription>The requester will be notified with your reason.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Reason *</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder="e.g. Requested equipment isn't available this week..." />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="reject-submit">
            {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DamageChargeDialog({ open, onOpenChange, request, onDone }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setAmount(''); setReason('');
  }, [open]);
  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error('Amount must be greater than 0'); return; }
    if ((reason || '').trim().length < 3) { toast.error('Reason (min 3 chars) required'); return; }
    setSaving(true);
    try {
      const { data } = await requestsAPI.chargeDamage(request.request_id, {
        amount: amt, reason: reason.trim(),
      });
      if (data.outcome === 'CAPTURED') {
        toast.success(`Damage charge captured (${data.charge_id})`);
      } else {
        toast.error(`Charge outcome: ${data.outcome}${data.error ? ' — ' + data.error : ''}`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Charge failed');
    } finally {
      setSaving(false);
    }
  };
  if (!request) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Charge for Damages</DialogTitle>
          <DialogDescription>
            Charge the card on file (last4 {request.deposit_card_last4 || '••••'}) for damage or loss.
            The customer is notified by email and SMS when the charge succeeds.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>Amount ($) *</Label>
            <Input type="number" min="0.01" step="0.01" value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   data-testid="damage-amount-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Reason *</Label>
            <Textarea rows={3} value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Banner torn beyond repair (asset KR-BAN-005)"
                      data-testid="damage-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700"
                  data-testid="damage-charge-submit">
            {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <DollarSign size={14} className="mr-1" />}
            Charge Card on File
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Request Row (expandable) + main tab
// ═══════════════════════════════════════════════════════════════════
function RequestRow({ req, isAdmin, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const canCancel = ['pending', 'approved', 'ready', 'preparing'].includes(req.status);
  const canApprove = isAdmin && req.status === 'pending';
  const canReject = isAdmin && req.status === 'pending';
  const canStartPreparing = isAdmin && req.status === 'approved';
  const canMarkReadyForPickup = isAdmin && ['preparing', 'approved'].includes(req.status);
  const canMarkPickedUp = isAdmin && req.status === 'ready_for_pickup';
  const canChargeDamage = isAdmin
    && ['picked_up', 'checked_out', 'returned'].includes(req.status)
    && !!req.deposit_payment_token;
  const canRefund = isAdmin
    && req.deposit_status === 'captured'
    && ['returned', 'picked_up', 'checked_out'].includes(req.status);

  const doAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      onAction?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded-md bg-white" data-testid={`request-row-${req.request_id}`}>
      <div className="p-3 flex items-center gap-3">
        <button type="button" onClick={() => setExpanded((x) => !x)}
                className="text-slate-400 hover:text-slate-700">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate">
              {req.event_label || `Request ${req.request_id.slice(0, 12)}`}
            </span>
            <StatusBadge status={req.status} />
            {req.is_urgent && ['pending', 'approved', 'preparing'].includes(req.status) && (
              <Badge className="bg-orange-100 text-orange-800 gap-1 inline-flex items-center">
                <AlertTriangle size={11} /> Urgent
              </Badge>
            )}
            {req.has_conflicts && req.status === 'pending' && (
              <Badge className="bg-rose-100 text-rose-800 gap-1 inline-flex items-center">
                <AlertTriangle size={11} /> Conflict
              </Badge>
            )}
            {req.deposit_status === 'captured' && (
              <Badge className="bg-emerald-100 text-emerald-800">Deposit captured</Badge>
            )}
            {req.deposit_status === 'refunded' && (
              <Badge className="bg-slate-100 text-slate-700">Deposit refunded</Badge>
            )}
            {req.deposit_status === 'partially_refunded' && (
              <Badge className="bg-amber-100 text-amber-800">Deposit partially refunded</Badge>
            )}
            {(req.deposit_status === 'declined' || req.deposit_status === 'error') && (
              <Badge className="bg-rose-100 text-rose-800 gap-1 inline-flex items-center">
                <AlertTriangle size={11} /> Deposit {req.deposit_status}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
            <span className="inline-flex items-center gap-1">
              <CalendarClock size={11} /> {formatDate(req.event_date)}
              {req.return_date !== req.event_date && ` → ${formatDate(req.return_date)}`}
            </span>
            <span>{req.items.length} line(s)</span>
            {(req.target_display_name || req.target_agent_name) && (
              <span>for {req.target_display_name || req.target_agent_name}</span>
            )}
            {req.requester_display_name && <span>by {req.requester_display_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {canApprove && (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => onAction?.('approve', req)} data-testid={`approve-${req.request_id}`}>
              <CheckCircle2 size={14} className="mr-1" /> Approve
            </Button>
          )}
          {canReject && (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => onAction?.('reject', req)} data-testid={`reject-${req.request_id}`}>
              <XCircle size={14} className="mr-1" /> Reject
            </Button>
          )}
          {canStartPreparing && (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => doAction(() => requestsAPI.startPreparing(req.request_id), 'Preparation started — go scan the items')}
                    data-testid={`start-preparing-${req.request_id}`}>
              <Package size={14} className="mr-1" /> Start Preparing
            </Button>
          )}
          {req.status === 'preparing' && isAdmin && (
            <Button size="sm" variant="default" disabled={busy}
                    onClick={() => {
                      const params = new URLSearchParams(window.location.search);
                      params.set('tab', 'checkout');
                      if (req.loan_id) params.set('loan_id', req.loan_id);
                      window.location.search = params.toString();
                    }}
                    data-testid={`scan-package-${req.request_id}`}>
              <Package size={14} className="mr-1" /> Scan &amp; Package
            </Button>
          )}
          {canMarkReadyForPickup && req.status !== 'preparing' && (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => doAction(() => requestsAPI.markReadyForPickup(req.request_id),
                                             'Marked ready — deposit charged')}
                    data-testid={`ready-for-pickup-${req.request_id}`}>
              <Clock size={14} className="mr-1" /> Ready for Pickup
            </Button>
          )}
          {canMarkPickedUp && (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => doAction(() => requestsAPI.markPickedUp(req.request_id), 'Picked up')}
                    data-testid={`mark-picked-up-${req.request_id}`}>
              <CheckCircle2 size={14} className="mr-1" /> Mark Picked Up
            </Button>
          )}
          {canChargeDamage && (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => onAction?.('damage', req)}
                    className="text-rose-600 border-rose-300 hover:bg-rose-50"
                    data-testid={`charge-damage-${req.request_id}`}>
              <DollarSign size={14} className="mr-1" /> Charge Damages
            </Button>
          )}
          {canRefund && (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => {
                      const amt = Number(req.deposit_amount || 0).toFixed(2);
                      if (!window.confirm(
                        `Release $${amt} deposit back to the card on file? `
                        + `This cannot be undone.`
                      )) return;
                      doAction(() => requestsAPI.refundDeposit(req.request_id, {}),
                               'Refund submitted');
                    }}
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    data-testid={`refund-deposit-${req.request_id}`}>
              <RotateCcw size={14} className="mr-1" /> Refund Deposit
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="ghost" disabled={busy}
                    onClick={() => {
                      if (!window.confirm('Cancel this request?')) return;
                      doAction(() => requestsAPI.cancel(req.request_id), 'Cancelled');
                    }}
                    className="text-rose-600">
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="border-t bg-slate-50/50 p-3 space-y-3">
          {/* Row 1: Target + Requester + Financial */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(req.target_agent || req.target_staff) && (
              <div className="bg-white rounded border p-2.5">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                  For {req.target_agent ? 'Agent' : 'Staff'}
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {req.target_agent?.name || req.target_staff?.name}
                </div>
                {req.target_agent?.npn && (
                  <div className="text-xs text-slate-600">NPN {req.target_agent.npn}</div>
                )}
                {req.target_agent?.agency_name && (
                  <div className="text-xs text-slate-600">{req.target_agent.agency_name}</div>
                )}
                {(req.target_agent?.email || req.target_staff?.email) && (
                  <div className="text-xs text-slate-600 break-all">
                    {req.target_agent?.email || req.target_staff?.email}
                  </div>
                )}
                {(req.target_agent?.phone || req.target_staff?.phone) && (
                  <div className="text-xs text-slate-600">
                    {req.target_agent?.phone || req.target_staff?.phone}
                  </div>
                )}
              </div>
            )}
            {req.requester_contact
              && req.requester_contact.name !== (req.target_agent?.name || req.target_staff?.name) && (
              <div className="bg-white rounded border p-2.5">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                  Submitted By
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {req.requester_contact.name}
                </div>
                {req.requester_contact.email && (
                  <div className="text-xs text-slate-600 break-all">{req.requester_contact.email}</div>
                )}
                {req.requester_contact.phone && (
                  <div className="text-xs text-slate-600">{formatPhone(req.requester_contact.phone)}</div>
                )}
              </div>
            )}
            <div className="bg-white rounded border p-2.5">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                Financial &amp; Deposit
              </div>
              <div className="text-sm text-slate-800 flex items-center gap-1">
                {req.financial_responsibility_accepted
                  ? <><CheckCircle2 size={13} className="text-emerald-600" /> Responsibility accepted</>
                  : <><AlertTriangle size={13} className="text-rose-600" /> NOT accepted</>}
              </div>
              <div className="text-sm text-slate-800 mt-1">
                Deposit: <strong>${Number(req.deposit_amount || 0).toFixed(2)}</strong>
                <span className="text-xs text-slate-500"> · cap ${Number(req.deposit_cap || 65).toFixed(2)}</span>
              </div>
              <div className="text-xs text-slate-600">
                Status: <span className="capitalize">{(req.deposit_status || 'not_collected').replace('_', ' ')}</span>
              </div>
              {req.deposit_card_last4 && (
                <div className="text-xs text-slate-600">
                  Card: {req.deposit_card_brand || 'Card'} ····{req.deposit_card_last4}
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Pickup + Return + Event */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white rounded border p-2.5">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                Pickup
              </div>
              <div className="text-sm text-slate-800 capitalize">
                {(req.pickup_type || 'kr_office').replace('_', ' ')}
              </div>
              {req.pickup_location && (
                <>
                  <div className="text-sm font-medium text-slate-900 mt-0.5">{req.pickup_location.name}</div>
                  {req.pickup_location.address && (
                    <div className="text-xs text-slate-600">
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(req.pickup_location.address)}`}
                        target="_blank" rel="noreferrer"
                        className="text-sky-600 underline">
                        {req.pickup_location.address}
                      </a>
                    </div>
                  )}
                  {req.pickup_location.contact_name && (
                    <div className="text-xs text-slate-600">Contact: {req.pickup_location.contact_name}</div>
                  )}
                  {req.pickup_location.contact_phone && (
                    <div className="text-xs text-slate-600">{req.pickup_location.contact_phone}</div>
                  )}
                  {req.pickup_location.pickup_hours?.[0]?.open && (
                    <div className="text-xs text-slate-600">
                      Hours: {req.pickup_location.pickup_hours[0].open}-{req.pickup_location.pickup_hours[0].close}
                    </div>
                  )}
                </>
              )}
              {(req.pickup_window_start || req.pickup_window_end) && (
                <div className="text-xs text-slate-600 mt-1">
                  Window: {req.pickup_window_start ? new Date(req.pickup_window_start).toLocaleString() : '—'}
                  {' → '}
                  {req.pickup_window_end ? new Date(req.pickup_window_end).toLocaleString() : '—'}
                </div>
              )}
            </div>
            <div className="bg-white rounded border p-2.5">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Return</div>
              {req.return_location ? (
                <>
                  <div className="text-sm font-medium text-slate-900">{req.return_location.name}</div>
                  {req.return_location.address && (
                    <div className="text-xs text-slate-600">
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(req.return_location.address)}`}
                        target="_blank" rel="noreferrer"
                        className="text-sky-600 underline">
                        {req.return_location.address}
                      </a>
                    </div>
                  )}
                  {req.return_location.dropoff_hours?.[0]?.open && (
                    <div className="text-xs text-slate-600">
                      Return Hours: {req.return_location.dropoff_hours[0].open}-{req.return_location.dropoff_hours[0].close}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-600">Same as pickup (default)</div>
              )}
              <div className="text-xs text-slate-500 mt-1">Due back {formatDate(req.return_date)}</div>
            </div>
            <div className="bg-white rounded border p-2.5">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Event</div>
              {req.linked_event ? (
                <>
                  <div className="text-sm font-medium text-slate-900">{req.linked_event.title}</div>
                  <div className="text-xs text-slate-600">{formatDate(req.linked_event.event_date)}</div>
                  {req.linked_event.address && (
                    <div className="text-xs text-slate-600">
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(req.linked_event.address)}`}
                        target="_blank" rel="noreferrer"
                        className="text-sky-600 underline">
                        {req.linked_event.address}
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-600">
                  {req.event_label || 'No linked event'}
                </div>
              )}
              <div className="text-xs text-slate-500 mt-1">
                {req.is_urgent
                  ? <span className="text-orange-600 font-medium">URGENT · {req.lead_business_days ?? '?'} biz days</span>
                  : `${req.lead_business_days ?? '?'} biz days lead time`}
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Items</div>
            <div className="space-y-1">
              {req.items.map((line, i) => {
                const short = (req.shortfalls || [])[i];
                return (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div>
                      <span className="font-medium">{line.type_name || line.type_id}</span>
                      <span className="text-slate-500"> × {line.quantity}</span>
                      {line.type_default_deposit != null && (
                        <span className="text-xs text-slate-500">
                          {' '}(${Number(line.type_default_deposit).toFixed(2)} each)
                        </span>
                      )}
                      {line.notes && <span className="text-slate-500"> — {line.notes}</span>}
                    </div>
                    {short && short.shortfall > 0 && req.status === 'pending' && (
                      <span className="text-xs text-rose-600">
                        Only {short.available_now}/{short.requested} available
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {req.assigned_asset_ids && req.assigned_asset_ids.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Assigned Assets</div>
              <div className="text-xs font-mono text-slate-700">
                {req.assigned_asset_ids.join(', ')}
              </div>
            </div>
          )}
          {req.notes && (
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Requester Notes</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.notes}</p>
            </div>
          )}
          {req.admin_notes && (
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Admin Notes</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.admin_notes}</p>
            </div>
          )}
          {req.rejection_reason && (
            <div>
              <div className="text-xs font-medium text-rose-600 uppercase tracking-wide mb-1">Rejection Reason</div>
              <p className="text-sm text-rose-700 whitespace-pre-wrap">{req.rejection_reason}</p>
            </div>
          )}
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Timeline</div>
            <div className="space-y-1 text-xs">
              {(req.timeline || []).map((t, i) => (
                <div key={i} className="text-slate-600">
                  <span className="font-medium capitalize">{t.action}</span>
                  {t.actor && <span className="text-slate-500"> by {t.actor}</span>}
                  {t.detail && <span className="text-slate-500"> — {t.detail}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RequestsTab() {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(isAdmin ? 'pending' : 'all');
  const [createOpen, setCreateOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [damageTarget, setDamageTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const { data } = await requestsAPI.list(params);
      setItems(data.items || []);
    } catch (e) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = (action, req) => {
    if (typeof action !== 'string') { load(); return; }
    if (action === 'approve') setApproveTarget(req);
    if (action === 'reject') setRejectTarget(req);
    if (action === 'damage') setDamageTarget(req);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="requests-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="preparing">Preparing</SelectItem>
              <SelectItem value="ready_for_pickup">Ready for Pickup</SelectItem>
              <SelectItem value="picked_up">Picked Up</SelectItem>
              <SelectItem value="checked_out">Checked Out</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">
            {items.length} request(s)
          </span>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-request-btn">
          <Plus size={16} className="mr-1" /> New Request
        </Button>
      </div>

      <Card>
        <CardContent className="p-3">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-2" />
              Loading requests...
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Package size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No requests</p>
              <p className="text-sm">
                {isAdmin ? 'No requests match this filter.' : 'Submit your first request for an upcoming event.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((r) => (
                <RequestRow key={r.request_id} req={r} isAdmin={isAdmin} onAction={handleAction} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateRequestDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      <ApproveDialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}
                     request={approveTarget} onDone={load} />
      <RejectDialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}
                    request={rejectTarget} onDone={load} />
      <DamageChargeDialog open={!!damageTarget} onOpenChange={(o) => !o && setDamageTarget(null)}
                          request={damageTarget} onDone={load} />
    </div>
  );
}
